// Chemin: C:\gestion-copro\src\components\sentinelle\ProtectionSentinelleModal.tsx
// Description: Modal de blocage SENTINELLE pour messages à risque élevé
// Affiche la reformulation proposée et permet de l'appliquer ou modifier
// Session 52 : création initiale - Spec SENTINELLE V2

import React, { useState } from 'react';
import {
  Shield,
  AlertTriangle,
  Check,
  Pencil,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface AlerteSCUD {
  type: string;
  message: string;
  extrait?: string;
  suggestion?: string;
}

interface ReformulationProposee {
  objet: string;
  contenu: string;
}

interface ProtectionSentinelleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Objet original du message */
  objetOriginal: string;
  /** Contenu original du message */
  contenuOriginal: string;
  /** Alertes détectées par l'analyse SCUD */
  alertes: AlerteSCUD[];
  /** Reformulation proposée par SENTINELLE */
  reformulationProposee?: ReformulationProposee;
  /** Callback quand l'utilisateur applique la reformulation */
  onAppliquerReformulation: (objet: string, contenu: string) => void;
  /** Callback quand l'utilisateur veut modifier lui-même */
  onModifierManuellement: () => void;
  /** Callback pour demander une nouvelle reformulation */
  onDemanderReformulation?: () => Promise<ReformulationProposee | null>;
  /** Indique si une reformulation est en cours de chargement */
  isLoadingReformulation?: boolean;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const ProtectionSentinelleModal: React.FC<ProtectionSentinelleModalProps> = ({
  isOpen,
  onClose,
  objetOriginal,
  contenuOriginal,
  alertes,
  reformulationProposee,
  onAppliquerReformulation,
  onModifierManuellement,
  onDemanderReformulation,
  isLoadingReformulation = false,
}) => {
  const [showAlertes, setShowAlertes] = useState(true);
  const [showComparaison, setShowComparaison] = useState(false);

  if (!isOpen) return null;

  // Grouper les alertes par type
  const alertesParType = alertes.reduce((acc, alerte) => {
    const type = alerte.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(alerte);
    return acc;
  }, {} as Record<string, AlerteSCUD[]>);

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      terme_risque: 'Termes à risque',
      accusation: 'Accusations',
      imperatif: 'Formulations impératives',
      menace: 'Menaces ou ultimatums',
      emotion: 'Contenu émotionnel',
      juridique: 'Risques juridiques',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      terme_risque: 'text-orange-700 bg-orange-50',
      accusation: 'text-red-700 bg-red-50',
      imperatif: 'text-amber-700 bg-amber-50',
      menace: 'text-red-700 bg-red-50',
      emotion: 'text-purple-700 bg-purple-50',
      juridique: 'text-red-700 bg-red-50',
    };
    return colors[type] || 'text-gray-700 bg-gray-50';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl">
          {/* Header - Rouge pour risque élevé */}
          <div className="bg-red-600 text-white px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Protection SENTINELLE</h2>
                  <p className="text-red-100 text-sm">
                    Message bloqué - Reformulation requise
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {/* Message d'explication */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-800 font-medium">
                    Ce message contient des formulations à risque élevé
                  </p>
                  <p className="text-red-700 text-sm mt-1">
                    SENTINELLE a détecté {alertes.length} problème{alertes.length > 1 ? 's' : ''} qui 
                    pourrai{alertes.length > 1 ? 'ent' : 't'} nuire au Conseil Syndical. 
                    Vous devez appliquer la reformulation ou modifier le message avant de continuer.
                  </p>
                </div>
              </div>
            </div>

            {/* Section alertes (dépliable) */}
            <div className="mb-6">
              <button
                onClick={() => setShowAlertes(!showAlertes)}
                className="flex items-center gap-2 text-gray-700 font-medium hover:text-gray-900 transition-colors"
              >
                {showAlertes ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                Problèmes détectés ({alertes.length})
              </button>

              {showAlertes && (
                <div className="mt-3 space-y-3">
                  {Object.entries(alertesParType).map(([type, alertesType]) => (
                    <div key={type} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className={`px-4 py-2 font-medium text-sm ${getTypeColor(type)}`}>
                        {getTypeLabel(type)} ({alertesType.length})
                      </div>
                      <div className="divide-y divide-gray-100">
                        {alertesType.map((alerte, index) => (
                          <div key={index} className="px-4 py-3 bg-white">
                            <p className="text-gray-800 text-sm">{alerte.message}</p>
                            {alerte.extrait && (
                              <div className="mt-2 px-3 py-2 bg-gray-50 rounded border-l-4 border-red-300">
                                <p className="text-gray-600 text-sm italic">"{alerte.extrait}"</p>
                              </div>
                            )}
                            {alerte.suggestion && (
                              <div className="mt-2 flex items-start gap-2">
                                <span className="text-green-600 text-xs font-medium uppercase">
                                  Suggestion :
                                </span>
                                <p className="text-green-700 text-sm">{alerte.suggestion}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reformulation proposée */}
            {reformulationProposee ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-green-800 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Version reformulée par SENTINELLE
                  </h3>
                  <button
                    onClick={() => setShowComparaison(!showComparaison)}
                    className="text-sm text-green-700 hover:text-green-800 underline"
                  >
                    {showComparaison ? 'Masquer' : 'Voir'} la comparaison
                  </button>
                </div>

                {showComparaison && (
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <p className="font-medium text-red-700 mb-1">Version originale</p>
                      <div className="bg-white border border-red-200 rounded p-3">
                        <p className="font-medium text-gray-800 mb-2">{objetOriginal}</p>
                        <p className="text-gray-600 whitespace-pre-wrap text-xs">{contenuOriginal}</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-green-700 mb-1">Version reformulée</p>
                      <div className="bg-white border border-green-200 rounded p-3">
                        <p className="font-medium text-gray-800 mb-2">{reformulationProposee.objet}</p>
                        <p className="text-gray-600 whitespace-pre-wrap text-xs">{reformulationProposee.contenu}</p>
                      </div>
                    </div>
                  </div>
                )}

                {!showComparaison && (
                  <div className="bg-white border border-green-200 rounded p-3">
                    <p className="font-medium text-gray-800 mb-2">
                      Objet : {reformulationProposee.objet}
                    </p>
                    <p className="text-gray-600 whitespace-pre-wrap text-sm">
                      {reformulationProposee.contenu}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">
                    {isLoadingReformulation 
                      ? 'Génération de la reformulation en cours...'
                      : 'Aucune reformulation disponible'}
                  </p>
                  {!isLoadingReformulation && onDemanderReformulation && (
                    <button
                      onClick={onDemanderReformulation}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Générer une reformulation
                    </button>
                  )}
                </div>
                {isLoadingReformulation && (
                  <div className="mt-3 flex justify-center">
                    <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer avec actions */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Vous ne pouvez pas envoyer ce message sans correction.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={onModifierManuellement}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  Modifier moi-même
                </button>
                {reformulationProposee && (
                  <button
                    onClick={() => onAppliquerReformulation(
                      reformulationProposee.objet,
                      reformulationProposee.contenu
                    )}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Appliquer cette version
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtectionSentinelleModal;
